import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutData, aboutFaqs } from '@/mocks/aboutData';

// Note: This is just a placeholder - the actual form URL will be obtained at runtime
let contactFormUrl = '';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'j9官方平台',
  url: 'https://j9guanwang.com/',
  description: 'j9是备受用户信赖的综合娱乐平台，提供丰富游戏体验、安全支付保障与极速提款服务。',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-9999',
    contactType: 'customer service',
    availableLanguage: 'Chinese',
  },
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于j9官方平台 - 品牌介绍、信誉与联系方式',
  description: aboutData.description,
  url: 'https://j9guanwang.com/about',
};

const aboutFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: aboutFaqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
};

export default function AboutPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const contactFormUrl = 'https://readdy.ai/api/form/d9q9q87nahb7382ov8cg';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpField = formData.get('phone_alt') as string;
    if (hpField && hpField.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('loading');

    try {
      const resp = await fetch(contactFormUrl || '#', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const respText = await resp.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(respText);
      } catch {
        // use raw text
      }

      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        const msg =
          (parsed?.meta as Record<string, string>)?.message ||
          (parsed?.message as string) ||
          '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg schema={organizationSchema} />
      <SchemaOrg schema={webpageSchema} />
      <SchemaOrg schema={aboutFaqSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-10 pb-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-4">
              关于<strong className="text-primary-500">j9平台</strong>
            </h1>
          </div>
        </div>

        {/* Brand Intro */}
        <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  {aboutData.brandName} · {aboutData.brandIntro}
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  {aboutData.description}
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  {aboutData.mission}
                </p>
              </div>
              <div className="flex-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20luxurious%20office%20building%20exterior%20with%20warm%20golden%20sunset%20lighting%20dark%20glass%20facade%20professional%20corporate%20headquarters%20premium%20atmosphere%20clean%20aesthetic&width=700&height=450&seq=about-brand&orientation=landscape"
                  alt="j9官方平台"
                  className="w-full rounded-xl object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full py-12 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {aboutData.reputationStats.map((stat, i) => (
                <div key={i} className="text-center p-6 rounded-xl bg-background-50">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-accent-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section id="security" className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                我们的<strong className="text-primary-500">核心价值观</strong>
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                四大核心价值观，指引j9平台不断前行
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutData.values.map((val, i) => (
                <div
                  key={i}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${val.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                    {val.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {val.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                发展<strong className="text-primary-500">历程</strong>
              </h2>
              <p className="text-foreground-600">从创立到行业领先，一步一个脚印</p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2"></div>
              <div className="space-y-8">
                {aboutData.milestones.map((milestone, i) => (
                  <div
                    key={i}
                    className={`relative flex items-center gap-6 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:flex flex-1 justify-end">
                      <div className={`max-w-xs ${i % 2 === 0 ? 'text-right mr-8' : 'text-left ml-8'}`}>
                        <p className="text-sm text-foreground-600">{milestone.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50 -translate-x-1/2 z-10"></div>
                    <div className="ml-12 md:ml-0 md:flex-1">
                      <div
                        className={`${
                          i % 2 === 0 ? 'md:hidden md:ml-8' : 'md:mr-8 md:text-right'
                        }`}
                      >
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-primary-500 text-background-50 mb-2">
                          {milestone.year}
                        </span>
                        <p className="text-sm text-foreground-600 md:hidden">{milestone.event}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Certificates */}
        <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                资质<strong className="text-primary-500">认证</strong>
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                多项权威认证，为您的娱乐安全保驾护航
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutData.certificates.map((cert, i) => (
                <div
                  key={i}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-verified-badge-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                    {cert.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {cert.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                <strong className="text-primary-500">联系</strong>我们
              </h2>
              <p className="text-foreground-600">随时为您提供贴心服务</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">客服电话</h3>
                    <p className="text-foreground-600 text-sm">{aboutData.contactInfo.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">电子邮箱</h3>
                    <p className="text-foreground-600 text-sm">{aboutData.contactInfo.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">微信公众号</h3>
                    <p className="text-foreground-600 text-sm">{aboutData.contactInfo.wechat}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">服务时间</h3>
                    <p className="text-foreground-600 text-sm">{aboutData.contactInfo.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">在线留言</h3>
                <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="about-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（限500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="form-hp-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* About FAQ */}
        <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                关于j9的<strong className="text-primary-500">常见问题</strong>
              </h2>
            </div>

            <div className="space-y-3">
              {aboutFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <div
                      className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                        faqOpen === index
                          ? 'bg-primary-500 text-background-50 rotate-45'
                          : 'bg-background-200 text-foreground-500'
                      }`}
                    >
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      faqOpen === index ? 'max-h-96 pb-5 px-5' : 'max-h-0'
                    }`}
                  >
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}